/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import {
  ArrowBack,
  ArrowForward,
  Check,
  EmojiEvents,
  Image,
  Info,
} from "@mui/icons-material"
import RaffleHeadForm from "../features/raffles/components/raffle-head-form"
import DialogRaffleError from "../features/raffles/components/dialog-raffle-error"
import RaffleImagesHandler from "../features/raffles/components/raffle-images-handler"
import RafflePrizesHandler from "../features/raffles/components/raffle-prizes-handler"
import { useNavigate, useParams } from "react-router-dom"
import { useEffect } from "react"
import { useAppDispatch, useAppSelector } from "../app/hooks"
import {
  getRaffleByIdThunk,
  resetFormAct,
  resetSuccessCreatedAct,
  setWizardStepAct,
} from "../features/raffles/handle-raffle.slice"
import { addRaffleToStackAct } from "../features/raffles-list/rafles-list.slice"
import Title from "../features/dashboard/components/Title"

const WIZARD_STEPS = [
  { label: "Datos básicos", icon: <Info fontSize="small" /> },
  { label: "Imágenes", icon: <Image fontSize="small" /> },
  { label: "Premios", icon: <EmojiEvents fontSize="small" /> },
]

export default function HandleRaffleView() {
  const { raffleId } = useParams()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))

  const { currentRaffle, wizardStep, successCreated } = useAppSelector((state) => state.raffle)
  const isEdit = Boolean(raffleId)
  const canAccessMediaSteps = currentRaffle !== undefined

  useEffect(() => {
    if (raffleId) {
      dispatch(getRaffleByIdThunk({ raffleId }))
    } else {
      dispatch(resetFormAct())
    }
  }, [raffleId])

  useEffect(() => {
    if (successCreated && currentRaffle) {
      dispatch(addRaffleToStackAct(currentRaffle))
      navigate(`/dashboard/handel-raffle/${currentRaffle._id}`, { replace: true })
      dispatch(resetSuccessCreatedAct())
    }
  }, [successCreated, currentRaffle])

  const handleStepClick = (step: number) => {
    if (step === 0 || canAccessMediaSteps) {
      dispatch(setWizardStepAct(step))
    }
  }

  const goBack = () => {
    if (wizardStep > 0) {
      dispatch(setWizardStepAct(wizardStep - 1))
    }
  }

  const goNext = () => {
    if (wizardStep < WIZARD_STEPS.length - 1 && canAccessMediaSteps) {
      dispatch(setWizardStepAct(wizardStep + 1))
    }
  }

  const finishWizard = () => {
    navigate("/dashboard/raffles")
  }

  return (
    <Stack spacing={1.5} sx={{ maxWidth: 960, mx: "auto" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Box>
          <Button
            size="small"
            startIcon={<ArrowBack />}
            onClick={() => navigate("/dashboard/raffles")}
            sx={{ mb: 0.5, cursor: "pointer", ml: -0.5 }}
          >
            Volver a sorteos
          </Button>
          <Title>{isEdit ? "Editar sorteo" : "Nuevo sorteo"}</Title>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.3 }}>
            {isEdit
              ? "Actualiza la información, galería y premios del sorteo."
              : "Completa los pasos para publicar un sorteo atractivo para tus clientes."}
          </Typography>
          {currentRaffle && (
            <Chip
              size="small"
              label={currentRaffle.name}
              color="primary"
              variant="outlined"
              sx={{ mt: 1, maxWidth: 280 }}
            />
          )}
        </Box>
      </Box>

      <Paper variant="outlined" sx={{ borderRadius: 2, px: { xs: 1, sm: 2 }, py: 2 }}>
        <Stepper
          activeStep={wizardStep}
          alternativeLabel={!isMobile}
          orientation={isMobile ? "vertical" : "horizontal"}
          sx={{ mb: wizardStep === 0 ? 0 : 1 }}
        >
          {WIZARD_STEPS.map((step, index) => (
            <Step
              key={step.label}
              completed={index < wizardStep || (index > 0 && canAccessMediaSteps && index <= wizardStep)}
              disabled={index > 0 && !canAccessMediaSteps}
              sx={{ cursor: index === 0 || canAccessMediaSteps ? "pointer" : "default" }}
              onClick={() => handleStepClick(index)}
            >
              <StepLabel
                optional={
                  index === 0 && !canAccessMediaSteps ? (
                    <Typography variant="caption">Requerido primero</Typography>
                  ) : undefined
                }
                StepIconProps={{
                  icon: index < wizardStep ? <Check fontSize="small" /> : index + 1,
                }}
              >
                {step.label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      <DialogRaffleError />

      <Paper variant="outlined" sx={{ borderRadius: 2, p: { xs: 1.5, sm: 2.5 } }}>
        {wizardStep === 0 && <RaffleHeadForm />}
        {wizardStep === 1 && canAccessMediaSteps && <RaffleImagesHandler embedded />}
        {wizardStep === 2 && canAccessMediaSteps && <RafflePrizesHandler embedded />}
      </Paper>

      {wizardStep > 0 && (
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 2,
            px: 2,
            py: 1.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="outlined"
            size="small"
            startIcon={<ArrowBack />}
            onClick={goBack}
            sx={{ cursor: "pointer" }}
          >
            Anterior
          </Button>
          <Stack direction="row" spacing={1}>
            {wizardStep < WIZARD_STEPS.length - 1 ? (
              <Button
                variant="contained"
                size="small"
                endIcon={<ArrowForward />}
                onClick={goNext}
                sx={{ cursor: "pointer" }}
              >
                Siguiente
              </Button>
            ) : (
              <Button
                variant="contained"
                color="success"
                size="small"
                endIcon={<Check />}
                onClick={finishWizard}
                sx={{ cursor: "pointer" }}
              >
                Finalizar
              </Button>
            )}
          </Stack>
        </Paper>
      )}
    </Stack>
  )
}
